import { useState, useCallback } from 'react';
import { COLOR_CATEGORY_MAP } from '../constants/colors';

const DEFAULT_FIELDS = {
  title: '',
  date: '',
  startTime: '',
  endTime: '',
  location: '',
  color: 'bg-blue-400',
  prepTime: '0 minutes',
};

// "2:00 PM" -> "14:00" — regex handles unicode spaces from toLocaleTimeString
function displayTimeTo24h(displayTime) {
  if (!displayTime || displayTime === 'All Day') return '';
  const match = displayTime.match(/(\d+):?(\d+)?\s*(AM|PM)/i);
  if (!match) return '';
  let h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  if (/pm/i.test(match[3]) && h !== 12) h += 12;
  if (/am/i.test(match[3]) && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Event {month, day, year} -> "YYYY-MM-DD"
function eventDateToISO(event) {
  return `${event.year}-${String(event.month).padStart(2, '0')}-${String(event.day).padStart(2, '0')}`;
}

export default function useEventForm() {
  const [fields, setFields] = useState({ ...DEFAULT_FIELDS });
  const [isSaving, setIsSaving] = useState(false);

  const setField = useCallback((name, value) => {
    setFields(prev => {
      if (name === 'startTime' && prev.startTime && prev.endTime && value) {
        const [oldH, oldM] = prev.startTime.split(':').map(Number);
        const [endH, endM] = prev.endTime.split(':').map(Number);
        const [newH, newM] = value.split(':').map(Number);
        const duration = (endH * 60 + endM) - (oldH * 60 + oldM);
        if (duration > 0) {
          const newEnd = newH * 60 + newM + duration;
          const clampedEnd = Math.min(newEnd, 23 * 60 + 59);
          const endTimeStr = `${String(Math.floor(clampedEnd / 60)).padStart(2, '0')}:${String(clampedEnd % 60).padStart(2, '0')}`;
          return { ...prev, startTime: value, endTime: endTimeStr };
        }
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const reset = useCallback(() => {
    setFields({ ...DEFAULT_FIELDS });
  }, []);

  const populate = useCallback((event) => {
    setFields({
      title: event.title || '',
      date: eventDateToISO(event),
      startTime: displayTimeTo24h(event.time),
      endTime: event.endTime || '',
      location: event.location || '',
      color: event.dotColor && COLOR_CATEGORY_MAP[event.dotColor]
        ? event.dotColor
        : 'bg-blue-400',
      prepTime: event.needsPrep ? '30 minutes' : '0 minutes',
    });
  }, []);

  return { fields, setField, reset, populate, isSaving, setIsSaving };
}
